import React, { useState, useRef, useEffect } from "react";
import { connect } from "react-redux";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { getDataFromAPI } from "../../../HandleAPICalls/actions";
import Sidebar from "../../Navigation/Sidebar/Sidebar";
import { toastr } from "react-redux-toastr";
import SubCategoryEditComponent from "./component";

const SubCategoryEditContainer = (props) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { categoryId, subCategoryId } = useParams();
  
  const category = location.state?.categoryData;
  const subCategory = location.state?.subCategoryData;
  
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    status: "active",
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const [imagePath, setImagePath] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isImageUploading, setIsImageUploading] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fileInputRef = useRef(null);

  // S3 Base URL
  const S3_BASE_URL = "https://s3.ap-south-1.amazonaws.com/daalchini/dcprod/";

  // Get full image URL
  const getFullImageUrl = (imagePath) => {
    if (!imagePath) return "";
    if (imagePath.startsWith("http://") || imagePath.startsWith("https://")) {
      return imagePath;
    }
    return `${S3_BASE_URL}${imagePath}`;
  };

  const isFormValid = formData.name.trim() !== "";

  // Get category ID - Priority: URL params > state data > location state
  const getCategoryId = () => {
    if (categoryId) return categoryId;
    if (category?.id) return category.id;
    if (location.state?.categoryData?.id) return location.state.categoryData.id;
    return null;
  };

  // Get sub category ID - Priority: URL params > state data > location state
  const getSubCategoryId = () => {
    if (subCategoryId) return subCategoryId;
    if (subCategory?.id) return subCategory.id;
    if (location.state?.subCategoryData?.id) return location.state.subCategoryData.id;
    return null;
  };

  // Upload image to S3
  const uploadImageToS3 = async (file) => {
    if (!file) return null;
    
    const formDataUpload = new FormData();
    formDataUpload.append("file", file);
    
    return new Promise((resolve, reject) => {
      props.getDataFromAPI(
        "/partner/api/v2/s3upload/machine-image",
        "POST",
        formDataUpload,
        (response) => {
          const uploadedPath = response?.image_path || response?.data?.image_path || response?.path || response?.url;
          
          if (!uploadedPath) {
            reject(new Error("No image path received from upload API"));
            return;
          }
          
          toastr.success("Success", "Image uploaded successfully");
          resolve(uploadedPath);
        },
        (err) => {
          toastr.error("Failed", "Unable to upload image");
          reject(err);
        }
      );
    });
  };

  // Fetch sub category details from API
  const fetchSubCategoryDetails = () => {
    const catId = getCategoryId();
    const subCatId = getSubCategoryId();
    
    if (!catId || !subCatId) {
      setError(`Missing IDs - Category ID: ${catId}, Sub Category ID: ${subCatId}`);
      setLoading(false);
      return;
    }

    setLoading(true);
    props.getDataFromAPI(
      `/partner/api/v1/product-categories/${catId}/subcategories/${subCatId}`,
      "GET",
      undefined,
      (response) => {
        if (response) {
          setFormData({
            name: response.name || "",
            description: response.description || "",
            status: response.active === true ? "active" : "inactive",
          });
          const imgUrl = getFullImageUrl(response.image || response.image_url);
          setImagePreview(imgUrl);
          setImagePath(response.image || response.image_url || "");
        }
        setLoading(false);
      },
      (err) => {
        console.error("Error fetching sub category:", err);
        setError(err?.message || "Failed to fetch sub category details");
        setLoading(false);
        toastr.error("Failed", "Unable to fetch sub category details");
      }
    );
  };

  // Initialize form data
  useEffect(() => {
  fetchSubCategoryDetails()
  }, [subCategory, categoryId, subCategoryId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleStatusChange = (status) => {
    setFormData(prev => ({ ...prev, status }));
  };

  const handleImageChange = async (file) => {
    if (!file) {
      setImagePreview("");
      setImagePath("");
      setImageFile(null);
      return;
    }

    if (!file.type.match('image.*')) {
      toastr.error("Error", "Please upload an image file");
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      toastr.error("Error", "Image size should be less than 5MB");
      return;
    }
    
    // Show preview immediately
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);
    
    // Upload image to S3
    setIsImageUploading(true);
    try {
      const uploadedPath = await uploadImageToS3(file);
      setImagePath(uploadedPath);
      setImageFile(file);
    } catch (error) {
      console.error("Image upload failed:", error);
      setImagePreview(getFullImageUrl(imagePath));
      toastr.error("Error", "Failed to upload image");
    } finally {
      setIsImageUploading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!isFormValid) {
      toastr.warning("Incomplete Form", "Please enter sub category name");
      return;
    }
    
    const catId = getCategoryId();
    const subCatId = getSubCategoryId();
    
    if (!catId || !subCatId) {
      toastr.error("Error", `Missing IDs - Category: ${catId}, Sub Category: ${subCatId}`);
      return;
    }
    
    setIsSubmitting(true);
    
    const payload = {
      name: formData.name,
      description: formData.description || "",
      image: imagePath || "",
      active: formData.status === "active",
    };
    
    props.getDataFromAPI(
      `/partner/api/v1/product-categories/${catId}/subcategories/${subCatId}`,
      "PUT",
      payload,
      (response) => {
        setIsSubmitting(false);
        setShowSuccessPopup(true);
        toastr.success("Success", "Sub category updated successfully!");
      },
      (error) => {
        setIsSubmitting(false);
        toastr.error("Error", error?.message || "Failed to update sub category");
      }
    );
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleCloseSuccessPopup = () => {
    setShowSuccessPopup(false);
    navigate(-1);
  };

  // Display loading state
  if (loading) {
    return (
      <div className="subcategory-form-container">
        <Sidebar />
        <div className="subcategory-form-content">
          <div className="subcategory-loading-state">
            <div className="spinner"></div>
            <p>Loading sub category details...</p>
          </div>
        </div>
      </div>
    );
  }

  // Display error state
  if (error) {
    return (
      <div className="subcategory-form-container">
        <Sidebar />
        <div className="subcategory-form-content">
          <div className="subcategory-error-state">
            <div className="error-icon">⚠️</div>
            <p>{error}</p>
            <button className="subcategory-error-btn" onClick={handleBack}>
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <SubCategoryEditComponent
      formData={formData}
      imagePreview={imagePreview}
      isSubmitting={isSubmitting || isImageUploading}
      isFormValid={isFormValid}
      category={category}
      subCategory={subCategory}
      onChange={handleChange}
      onStatusChange={handleStatusChange}
      onImageChange={handleImageChange}
      onSubmit={handleSubmit}
      onBack={handleBack}
      fileInputRef={fileInputRef}
      showSuccessPopup={showSuccessPopup}
      onSuccessClose={handleCloseSuccessPopup}
      categoryName={category?.name || location.state?.categoryData?.name}
    />
  );
};

function mapStateToProps(state) {
  return { props: state };
}

export default connect(mapStateToProps, { getDataFromAPI })(SubCategoryEditContainer);